import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  CreateEnvironmentInput,
  Environment,
  ListEnvironmentsParams,
  UpdateEnvironmentInput,
} from './environments-types';

const KEYS = {
  all: ['environments'] as const,
  list: (params?: ListEnvironmentsParams) =>
    [...KEYS.all, 'list', params?.is_active ?? 'all'] as const,
};

export function useEnvironments(params?: ListEnvironmentsParams) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: async () => {
      const r = await apiClient.get<Environment[]>('/admin/environments', {
        params: {
          ...(params?.is_active !== undefined ? { is_active: params.is_active } : {}),
        },
      });
      return r.data;
    },
  });
}

export function useCreateEnvironment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEnvironmentInput) =>
      apiClient.post<Environment>('/admin/environments', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUpdateEnvironment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEnvironmentInput }) =>
      apiClient.patch<Environment>(`/admin/environments/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useDeleteEnvironment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/environments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}
